<script lang="ts">
export default {
  name: 'AccountAnalytics',
};
</script>

<script setup lang="ts">
import { computed, ref, onMounted, inject, watch, watchEffect, provide } from 'vue';
import { tableauService, volatilityModelService, stockDropModelService, cyberCubeService } from '@/services/api';
import { documentService } from '@/services/v2/DocumentService';
import { useRoute, useRouter } from 'vue-router';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { TableauDashboard, TableauType } from '@/types/TableauDashboard';
import { VolatilityModel } from '@/types/VolatilityModel';
import { Document, DocumentDomainTypeAnalytics, DocumentType } from '@/types/v2/Document';
import {
  HandshakeIcon,
  HouseIcon,
  MegaphoneIcon,
  ShieldQuestionIcon,
  TreePalmIcon,
  LaptopMinimalIcon,
  ChevronRightIcon,
  LayersIcon,
  TagIcon,
  StarIcon,
} from '@lucide/vue';
import { useFavorites } from '@/composables/useFavorites';
import { StockDropModel } from '@/types/StockDropModel';
import AppButton from '@/components/v2/AppButton.vue';
import CasualtyIcon from '@/components/icons/CasualtyIcon.vue';
import { IsUserAClient } from '@/utilities/AuthUtils.js';
import { orgName } from '@/utilities/OrgConfig';
import { Category, UploadType, getTypeLabel } from '@/utilities/Analytics.js';
import { CyberModelCard } from '@/types/analytics/Analytics.js';
import { AccordionContent, AccordionHeader, AccordionItem, AccordionRoot, AccordionTrigger } from 'reka-ui';
import TagService from '@/services/TagService';

// -----
// data
// -----

const loading = ref<boolean>(true);
const dashboards = ref<TableauDashboard[]>([]);
const tableauBar = ref<HTMLElement | null>(null);
const tableauVizHeight = ref('');
const store = inject<Store<AppState>>('store');
const route = useRoute();
const router = useRouter();
const selectedDashboard = ref<TableauDashboard | null>(null);
const analyticsTypes = ref(new Map<number, string>());
const selectedCategoryType = ref<number>(-1);
const volatilityModels = ref<VolatilityModel[]>([]);
const stockDropModels = ref<StockDropModel[]>([]);
const cyberModels = ref<CyberModelCard[]>([]);
const documents = ref<Document[]>([]);
const documentTypes = ref<DocumentType[]>([]);
const tableauTypes = ref<TableauType[]>([]);
const tags = ref<string[]>([]);
const activeTypeFilters = ref<string[]>([]);
const activeTagFilters = ref<string[]>([]);
const { fetchFavorites, isFavorited, toggleFavorite, getFavoritedIds } = useFavorites();

// ---------
// watchers
// ---------
watchEffect(() => {
  // Go back to the dashboard if the user navigates to the analytics page
  if (route.path === `/account/${route.params.id}/analytics`) {
    selectedCategoryType.value = -1;
  }
});

watch(
  () => route.params.categoryId,
  async (category) => {
    if (category && category !== 'favorites') {
      selectedCategoryType.value = parseInt(category as string);
    }
  },
);

// -----------
// lifecycles
// -----------

onMounted(async () => {
  loading.value = true;
  computeTableauHeight();
  if (!IsUserAClient(store?.state.user)) {
    await fetchFavorites();
  }

  if (route.params.categoryId && route.params.categoryId !== 'favorites') {
    selectedCategoryType.value = parseInt(route.params.categoryId as string);
  }

  const category = route.meta.categoryType;
  if (category) {
    Array.from(analyticsTypes.value.entries()).forEach((t) => {
      if (t[1] === category) {
        selectedCategoryType.value = t[0];
      }
    });
  }

  await refresh();
  loading.value = false;
});

// --------
// methods
// --------

async function fetchMetadata() {
  const types = await tableauService.getAnalyticsTypes();
  analyticsTypes.value = types.reduce((acc, t) => {
    acc.set(t.id, t.name);
    return acc;
  }, new Map<number, string>());
}

function handleSelectResourceType(type: number) {
  selectedCategoryType.value = type;

  router.push({ path: `/account/${route.params.id}/analytics/category/${type}` });
  activeTypeFilters.value = [];
  activeTagFilters.value = [];
}

function handleSelectFavorites() {
  selectedCategoryType.value = -1;
  router.push({ path: `/account/${route.params.id}/analytics/category/favorites` });
  activeTypeFilters.value = [];
  activeTagFilters.value = [];
}

function openAnalyticsMailto() {
  window.location.href = 'mailto:analytics@cacgroup.com';
}

async function refresh() {
  loading.value = true;

  await Promise.all([
    fetchMetadata(),
    fetchVolatilityModels(),
    fetchStockDropModels(),
    fetchTableauDashboards(),
    fetchDocuments(),
    fetchDocumentTypes(),
    fetchTableauTypes(),
    fetchCyberModels(),
    fetchTags(),
  ]);

  if (route.query.id) {
    const selectedDashboardId = parseInt(route.query.id as string);
    selectedDashboard.value = dashboards.value.find((dashboard) => dashboard.id === selectedDashboardId) || null;
  }

  loading.value = false;
}

async function fetchTableauDashboards() {
  dashboards.value = await tableauService.getDashboards(parseInt(route.params.id as string));
}

async function fetchVolatilityModels() {
  volatilityModels.value = await volatilityModelService.findByAccountId(parseInt(route.params.id as string));
}

async function fetchStockDropModels() {
  const accountId = parseInt(route.params.id as string);
  stockDropModels.value = await stockDropModelService.findByAccountId(accountId);
}

async function fetchCyberModels() {
  const details = await cyberCubeService.getCyberModelDetailsByAccount(parseInt(route.params.id as string));
  cyberModels.value = details.map((model) => {
    return {
      accountId: model.accountId,
      id: model.id,
      title: `${model.accountName} Cyber Model`,
      type: 'cyber',
      client: model.accountName,
      created_by: model.createdBy ? model.createdBy : undefined,
      created_on: model.createdDate,
      updated_on: model.updatedDate,
      published: model.published,
    };
  });
}

async function fetchDocuments() {
  // domain ID 3 = Analytics
  documents.value = await documentService.getDocuments({
    documentDomainId: 3,
    accountId: parseInt(route.params.id as string),
  });
}

async function fetchTags() {
  tags.value = (await TagService.getTags()).map((tag) => tag.name);
}

async function fetchDocumentTypes() {
  documentTypes.value = await documentService.getDocumentTypes();
}

async function fetchTableauTypes() {
  tableauTypes.value = await tableauService.getTableauTypes();
}

function computeTableauHeight() {
  const calculatedHeight =
    window.innerHeight -
    (store?.state?.resizerRegistry['header']?.height ?? 0) -
    (tableauBar.value?.offsetHeight ?? 170);
  tableauVizHeight.value = `${calculatedHeight}px`;
}

function toggleTypeFilter(filter: string) {
  if (activeTypeFilters.value.includes(filter)) {
    activeTypeFilters.value = activeTypeFilters.value.filter((f) => f !== filter);
  } else {
    activeTypeFilters.value.push(filter);
  }
}

function clearTypeFilters() {
  activeTypeFilters.value = [];
}

function toggleTagFilter(filter: string) {
  if (activeTagFilters.value.includes(filter)) {
    activeTagFilters.value = activeTagFilters.value.filter((f) => f !== filter);
  } else {
    activeTagFilters.value.push(filter);
  }
}

function clearTagFilters() {
  activeTagFilters.value = [];
}

function updated() {
  refresh();
}

// ---------
// computed
// ---------

const isFavoritesCategory = computed(() => route.params.categoryId === 'favorites');

const selectedCategoryName = computed<string>(() => {
  if (isFavoritesCategory.value) return 'Favorites';
  return analyticsTypes.value.get(selectedCategoryType.value) ?? '';
});

const filteredDashboards = computed<TableauDashboard[]>(() => {
  if (isFavoritesCategory.value) {
    const favIds = getFavoritedIds('analytics-tableau');
    return (dashboards.value ?? []).filter((d) => favIds.has(String(d.id)));
  }

  // Advisory is a special case where we show dashboards not associated with an account
  if (selectedCategoryName.value === Category.Advisory) {
    if (activeTypeFilters.value.length > 0) {
      return dashboards.value.filter(
        (dashboard) => dashboard.accountId === null && activeTypeFilters.value.includes(dashboard.tableauType),
      );
    }
    return dashboards.value.filter((dashboard) => dashboard.accountId === null);
  }

  const accountId = parseInt(route.params.id as string);
  if (activeTypeFilters.value.length > 0) {
    return dashboards.value.filter(
      (dashboard) =>
        dashboard.analyticsTypeId === selectedCategoryType.value &&
        dashboard.accountId === accountId &&
        activeTypeFilters.value.includes(dashboard.tableauType),
    );
  }
  return dashboards.value.filter(
    (dashboard) => dashboard.analyticsTypeId === selectedCategoryType.value && dashboard.accountId === accountId,
  );
});

const filteredDocuments = computed<Document[]>(() => {
  if (isFavoritesCategory.value) {
    const favIds = getFavoritedIds('analytics-document');
    return (documents.value ?? []).filter((d) => favIds.has(d.documentId));
  }

  let filteredDocs = documents.value;

  // Advisory is a special case where we show documents not associated with an account
  if (selectedCategoryName.value === Category.Advisory) {
    // Filter by type
    if (activeTypeFilters.value.length > 0) {
      filteredDocs = filteredDocs.filter(
        (document) => document.accountId === null && activeTypeFilters.value.includes(document.documentTypeName),
      );
    } else {
      filteredDocs = filteredDocs.filter((document) => document.accountId === null);
    }

    // Filter by tags
    if (activeTagFilters.value.length > 0) {
      filteredDocs = filteredDocs.filter((d) => {
        return d.tags && d.tags.some((tag) => activeTagFilters.value.includes(tag));
      });
    }

    return filteredDocs;
  }

  // Filter by type
  const accountId = parseInt(route.params.id as string);
  if (activeTypeFilters.value.length > 0) {
    filteredDocs = filteredDocs.filter(
      (document) =>
        document.analyticsTypeId === selectedCategoryType.value &&
        document.accountId === accountId &&
        activeTypeFilters.value.includes(document.documentTypeName),
    );
  } else {
    filteredDocs = filteredDocs.filter(
      (document) => document.analyticsTypeId === selectedCategoryType.value && document.accountId === accountId,
    );
  }

  // Filter by tags
  if (activeTagFilters.value.length > 0) {
    filteredDocs = filteredDocs.filter((d) => {
      return d.tags && d.tags.some((tag) => activeTagFilters.value.includes(tag));
    });
  }

  return filteredDocs;
});

const filteredDocumentTypes = computed<DocumentType[]>(() => {
  return documentTypes.value.filter((type) => type.analyticsTypeId === selectedCategoryType.value);
});

const documentTypesForFilters = computed<DocumentType[]>(() => {
  // Advisory is a special case where we show all document types
  if (selectedCategoryName.value === Category.Advisory) {
    const uniqueTypes = new Map();
    documentTypes.value.forEach((type) => {
      if (
        type.domainName === DocumentDomainTypeAnalytics &&
        type.name !== 'Miscellaneous' &&
        !uniqueTypes.has(type.name)
      ) {
        uniqueTypes.set(type.name, type);
      }
    });
    return [
      ...uniqueTypes.values(),
      {
        id: 0,
        name: 'Miscellaneous',
        domainId: 3,
        domainName: 'Analytics',
      },
    ];
  }

  return documentTypes.value.filter((type) => type.analyticsTypeId === selectedCategoryType.value);
});

const filteredTableauTypes = computed<TableauType[]>(() => {
  return tableauTypes.value.filter((type) => type.analyticsTypeId === selectedCategoryType.value);
});

const filteredVolatilityModels = computed<VolatilityModel[]>(() => {
  if (isFavoritesCategory.value) {
    const favIds = getFavoritedIds('analytics-volatility');
    return (volatilityModels.value ?? []).filter((m) => favIds.has(String(m.id)));
  }
  if (selectedCategoryName.value === Category.Other) {
    if (activeTypeFilters.value.length > 0 && !activeTypeFilters.value.includes('Volatility Model')) {
      return [];
    }
    return volatilityModels.value;
  }
  return [];
});

const filteredStockDropModels = computed<StockDropModel[]>(() => {
  if (isFavoritesCategory.value) {
    const favIds = getFavoritedIds('analytics-stockdrop');
    return (stockDropModels.value ?? []).filter((m) => favIds.has(String(m.id)));
  }
  if (selectedCategoryName.value === Category.FLP) {
    if (activeTypeFilters.value.length > 0 && !activeTypeFilters.value.includes('Stock Drop Model')) {
      return [];
    }
    return stockDropModels.value;
  }
  return [];
});

const filteredCyberModels = computed<CyberModelCard[]>(() => {
  if (isFavoritesCategory.value) {
    const favIds = getFavoritedIds('analytics-cyber');
    return (cyberModels.value ?? []).filter((m) => favIds.has(String(m.id)));
  }
  if (selectedCategoryName.value === Category.Cyber) {
    if (activeTypeFilters.value.length > 0 && !activeTypeFilters.value.includes('Cyber Model')) {
      return [];
    }
    return cyberModels.value;
  }
  return [];
});

const isAClientAccount = computed(() => {
  return IsUserAClient(store?.state.user);
});

const visibleAnalyticsCategories = computed(() => {
  if (!isAClientAccount.value) {
    return analyticsTypes.value;
  }

  const accountId = parseInt(route.params.id as string);
  const categories = new Map<number, string>();

  analyticsTypes.value.forEach((name, id) => {
    let hasDashboards = false;
    let hasDocuments = false;

    // Advisory is a special case - show if there are published advisory resources (accountId === null)
    if (name === Category.Advisory) {
      hasDashboards = dashboards.value?.some((dashboard) => dashboard.accountId === null) ?? false;
      hasDocuments = documents.value?.some((document) => document.accountId === null) ?? false;
    } else {
      // For other categories, check for resources associated with this account
      hasDashboards = dashboards.value?.some((dashboard) => dashboard.analyticsTypeId === id && dashboard.accountId === accountId) ?? false;
      hasDocuments = documents.value?.some(
        (document) => document.analyticsTypeId === id && document.accountId === accountId,
      ) ?? false;
    }

    let hasAdditionalData = false;
    if (name === Category.FLP) {
      // Stock drop models only exist for FLP
      hasAdditionalData = stockDropModels.value?.length > 0;
    } else if (name === Category.Other) {
      // Volatility models only exist for Other
      hasAdditionalData = volatilityModels.value?.length > 0;
    } else if (name === Category.Cyber) {
      // Cyber models only exist for Cyber
      hasAdditionalData = cyberModels.value?.length > 0;
    }

    if (hasDashboards || hasDocuments || hasAdditionalData) {
      categories.set(id, name);
    }
  });

  return categories;
});

const documentsForCategory = computed(() => {
  if (selectedCategoryName.value === Category.Advisory) {
    return documents.value.filter((document) => document.accountId === null);
  }

  const accountId = parseInt(route.params.id as string);
  return documents.value.filter(
    (document) => document.accountId === accountId && document.analyticsTypeId === selectedCategoryType.value,
  );
});

const dashboardsForCategory = computed(() => {
  if (selectedCategoryName.value === Category.Advisory) {
    return dashboards.value.filter((dashboard) => dashboard.accountId === null);
  }

  const accountId = parseInt(route.params.id as string);
  return dashboards.value.filter(
    (dashboard) => dashboard.accountId === accountId && dashboard.analyticsTypeId === selectedCategoryType.value,
  );
});

const visibleDocumentFilters = computed(() => {
  return documentTypesForFilters.value.filter((type) =>
    documentsForCategory.value.some((doc) => doc.documentTypeName === type.name),
  );
});

const visibleTableauFilters = computed(() => {
  return filteredTableauTypes.value.filter((type) =>
    dashboardsForCategory.value.some((dashboard) => dashboard.tableauType === type.name),
  );
});

const hasVolatilityModels = computed(() => {
  return selectedCategoryName.value === Category.Other && volatilityModels.value?.length > 0;
});

const hasStockDropModels = computed(() => {
  return selectedCategoryName.value === Category.FLP && stockDropModels.value?.length > 0;
});

const hasCyberModels = computed(() => {
  return selectedCategoryName.value === Category.Cyber && cyberModels.value?.length > 0;
});

// -----
// provide
// -----
provide('selectedCategoryType', selectedCategoryType);
provide('isFavoritesCategory', isFavoritesCategory);
provide('isFavorited', isFavorited);
provide('toggleFavorite', toggleFavorite);
</script>

<template>
  <div v-if="selectedCategoryType < 0 && !isFavoritesCategory" class="flex flex-col items-center gap-4 pt-8">
    <div class="flex flex-col items-center">
      <h2>Analytics Resource</h2>
      <div>Please choose from one of the categories below</div>
    </div>

    <div class="flex w-120 flex-wrap justify-center gap-4 pt-2">
      <div v-if="!isAClientAccount" class="group tooltip pointer-events-auto z-20 cursor-pointer">
        <div
          class="flex h-24 w-36 flex-col justify-center rounded-lg border border-solid border-blue-200 bg-white shadow-md transition-all duration-150 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:shadow-none"
          @click="handleSelectFavorites()">
          <div class="text-black-500 flex flex-col items-center gap-2 self-center p-2">
            <StarIcon class="text-gray-400" />
            <h2 class="text-center text-sm font-medium">Favorites</h2>
          </div>
        </div>
      </div>
      <template v-for="analyticsType in visibleAnalyticsCategories.entries()" v-bind:key="analyticsType[0]">
        <div class="group tooltip pointer-events-auto z-20 cursor-pointer">
          <div
            class="flex h-24 w-36 flex-col justify-center rounded-lg border border-solid border-blue-200 bg-white shadow-md transition-all duration-150 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:shadow-none"
            @click="handleSelectResourceType(analyticsType[0])">
            <div class="text-black-500 flex flex-col items-center gap-2 self-center p-2">
              <HouseIcon v-if="analyticsType[1] === Category.Property" class="text-gray-400" />
              <CasualtyIcon v-if="analyticsType[1] === Category.Casualty" class="h-7 w-7 fill-gray-400" />
              <HandshakeIcon v-if="analyticsType[1] === Category.FLP" class="text-gray-400" />
              <TreePalmIcon v-if="analyticsType[1] === Category.Captives" class="text-gray-400" />
              <ShieldQuestionIcon v-if="analyticsType[1] === Category.Other" class="text-gray-400" />
              <MegaphoneIcon v-if="analyticsType[1] === Category.Advisory" class="text-gray-400" />
              <LaptopMinimalIcon v-if="analyticsType[1] === Category.Cyber" class="text-gray-400" />

              <h2 class="text-center text-sm font-medium">{{ analyticsType[1] }}</h2>
            </div>
          </div>
        </div>
      </template>
    </div>
    <AppButton
      primary
      class="mt-6"
      @click="openAnalyticsMailto">
      Contact {{ orgName() }} Analytics
    </AppButton>
  </div>
  <div v-if="selectedCategoryType > -1 || isFavoritesCategory" class="relative flex h-full">
    <aside class="min-w-60 max-w-60 border-r border-gray-300 bg-gray-100 p-4">
      <nav class="sticky top-20">
        <h2 class="text-sm font-semibold tracking-wide text-gray-700 uppercase">Categories</h2>

        <div class="pt-1 pb-8">
          <div
            v-if="!isAClientAccount"
            role="button"
            class="rounded-md border border-transparent px-3 py-1.5 text-sm transition-all select-none hover:bg-gray-200"
            :class="{ 'bg-specialty-teal/30 hover:bg-specialty-teal/30 font-medium': isFavoritesCategory }"
            @click="handleSelectFavorites()">
            Favorites
          </div>
          <div
            v-for="(analyticsType, index) in visibleAnalyticsCategories.entries()"
            :key="analyticsType[0]"
            :aria-label="analyticsType[1]"
            :tabindex="index"
            role="button"
            class="rounded-md border border-transparent px-3 py-1.5 text-sm transition-all select-none hover:bg-gray-200"
            :class="{
              'bg-specialty-teal/30 font-medium hover:bg-specialty-teal/30':
                selectedCategoryType === analyticsType[0] && !isFavoritesCategory,
            }"
            @click="handleSelectResourceType(analyticsType[0])">
            {{ analyticsType[1] }}
          </div>
        </div>

        <!-- filters -->
        <AccordionRoot v-if="!isFavoritesCategory" type="multiple" collapsible class="flex flex-col gap-4">
          <AccordionItem value="typeFilters">
            <AccordionHeader>
              <AccordionTrigger
                class="group flex w-full items-center justify-between text-left">
                <div class="flex items-center gap-2">
                  <ChevronRightIcon
                    class="h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform duration-30 group-data-[state=open]:rotate-90" />
                  <h2 class="text-sm font-semibold tracking-wide text-gray-700 uppercase">Types</h2>
                  <span v-if="activeTypeFilters.length > 0" class="badge badge-soft badge-sm">{{
                    activeTypeFilters.length
                  }}</span>
                  <button
                    v-visible="activeTypeFilters.length > 0"
                    class="btn rounded-lg bg-agency-red/70 text-white btn-xs"
                    @click.stop="clearTypeFilters">
                    Clear
                  </button>
                </div>
              </AccordionTrigger>
            </AccordionHeader>
            <AccordionContent class="overflow-hidden">
              <div class="flex flex-col gap-0.5">
                <!-- Document Filters -->
                <div
                  v-for="(type, i) in visibleDocumentFilters"
                  :key="type.id"
                  :aria-label="type.name"
                  :tabindex="i"
                  data-testid="doc-filter"
                  role="button"
                  class="rounded px-2 py-1.5 text-sm transition-all duration-75 hover:bg-gray-200"
                  :class="{
                    'bg-specialty-teal/20 font-medium hover:bg-specialty-teal/20': activeTypeFilters.includes(
                      type.name,
                    ),
                  }"
                  @click="toggleTypeFilter(type.name)">
                  <div class="flex items-start gap-2">
                    <LayersIcon class="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                    <div class="flex flex-col gap-0.5">
                      <span>{{ type.name }}</span>
                      <span class="text-xs text-gray-600 italic">{{
                        getTypeLabel(type.name, UploadType.Document)
                      }}</span>
                    </div>
                  </div>
                </div>

                <!-- Tableau Type Filters -->
                <div
                  v-for="(type, i) in visibleTableauFilters"
                  :key="type.id"
                  :aria-label="type.name"
                  :tabindex="i"
                  role="button"
                  class="rounded px-2 py-1.5 text-sm transition-all duration-75 hover:bg-gray-200"
                  :class="{
                    'bg-specialty-teal/20 font-medium hover:bg-specialty-teal/20': activeTypeFilters.includes(
                      type.name,
                    ),
                  }"
                  @click="toggleTypeFilter(type.name)">
                  <div class="flex items-start gap-2">
                    <LayersIcon class="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                    <div class="flex flex-col gap-0.5">
                      <span>{{ type.name }}</span>
                      <span class="text-xs text-gray-600 italic">{{
                        getTypeLabel(type.name, UploadType.TableauDashboard)
                      }}</span>
                    </div>
                  </div>
                </div>

                <!-- Volatility Model Filter (only shows for Other) -->
                <div
                  v-if="hasVolatilityModels"
                  role="button"
                  class="rounded px-2 py-1.5 text-sm transition-all duration-75 hover:bg-gray-200"
                  :class="{
                    'bg-specialty-teal/30 hover:bg-specialty-teal/30':
                      activeTypeFilters.includes('Volatility Model'),
                  }"
                  @click="toggleTypeFilter('Volatility Model')">
                  <div class="flex items-center gap-2">
                    <LayersIcon class="h-4 w-4 shrink-0 text-gray-500" />
                    <span>Volatility Model</span>
                  </div>
                </div>

                <!-- Stock Drop Model Filter (only shows for FLP) -->
                <div
                  v-if="hasStockDropModels"
                  role="button"
                  class="rounded px-2 py-1.5 text-sm transition-all duration-75 hover:bg-gray-200"
                  :class="{
                    'bg-specialty-teal/30 font-medium hover:bg-specialty-teal/30':
                      activeTypeFilters.includes('Stock Drop Model'),
                  }"
                  @click="toggleTypeFilter('Stock Drop Model')">
                  <div class="flex items-center gap-2">
                    <LayersIcon class="h-4 w-4 shrink-0 text-gray-500" />
                    <span>Stock Drop Model</span>
                  </div>
                </div>

                <!-- Cyber Model Filter (only shows for Cyber) -->
                <div
                  v-if="hasCyberModels"
                  role="button"
                  class="rounded px-2 py-1.5 text-sm transition-all duration-75 hover:bg-gray-200"
                  :class="{
                    'bg-specialty-teal/30 font-medium hover:bg-specialty-teal/30':
                      activeTypeFilters.includes('Cyber Model'),
                  }"
                  @click="toggleTypeFilter('Cyber Model')">
                  <div class="flex items-center gap-2">
                    <LayersIcon class="h-4 w-4 shrink-0 text-gray-500" />
                    <span>Cyber Model</span>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="tagFilters">
            <AccordionHeader>
              <AccordionTrigger class="group flex w-full items-center justify-between text-left">
                <div class="flex items-center gap-2">
                  <ChevronRightIcon
                    class="h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform duration-30 group-data-[state=open]:rotate-90" />
                  <h2 class="text-sm font-semibold tracking-wide text-gray-700 uppercase">Tags</h2>
                  <span v-if="activeTagFilters.length > 0" class="badge badge-soft badge-sm">{{
                    activeTagFilters.length
                  }}</span>
                  <button
                    v-visible="activeTagFilters.length > 0"
                    class="btn rounded-lg bg-agency-red/70 text-white btn-xs"
                    @click.stop="clearTagFilters">
                    Clear
                  </button>
                </div>
              </AccordionTrigger>
            </AccordionHeader>
            <AccordionContent class="overflow-hidden">
              <div class="flex flex-col gap-0.5">
                <!-- Tags -->
                <div
                  v-for="(tag, i) in tags"
                  :key="tag"
                  :aria-label="tag"
                  :tabindex="i"
                  role="button"
                  class="rounded px-2 py-1.5 text-sm transition-all duration-75 hover:bg-gray-200"
                  :class="{
                    'bg-specialty-teal/20 font-medium hover:bg-specialty-teal/20':
                      activeTagFilters.includes(tag),
                  }"
                  @click="toggleTagFilter(tag)">
                  <div class="flex items-start gap-2">
                    <TagIcon class="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                    <div class="flex flex-col gap-0.5">
                      {{ tag }}
                    </div>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </AccordionRoot>
      </nav>
    </aside>

    <router-view
      :loading="loading"
      :dashboards="filteredDashboards"
      :volatility-models="filteredVolatilityModels"
      :stock-drop-models="filteredStockDropModels"
      :cyber-models="filteredCyberModels"
      :documentTypes="filteredDocumentTypes"
      :tableau-types="filteredTableauTypes"
      :documents="filteredDocuments"
      :selectedCategoryType="selectedCategoryType"
      :selectedCategoryName="selectedCategoryName"
      :accountId="parseInt(route.params.id as string)"
      @updated="updated()" />
  </div>
</template>
